import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

interface Entry {
  id: string;
  amount: string;
  reason: string;
  staffName?: string;
  createdAt: string;
}

/**
 * Money taken out of the till for small purchases. Every entry is subtracted
 * from what cash-up expects to find in the drawer, so the variance stays
 * honest — without this, the drawer reads short every time money leaves it.
 */
export default function PettyCashPanel({
  branchId,
  shiftId,
  onChange,
}: {
  branchId: string;
  shiftId?: string;
  onChange?: () => void;
}) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!branchId) return;
    try {
      const res = await apiClient.listPettyCash(branchId, shiftId);
      setEntries(Array.isArray(res.data) ? res.data : []);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [branchId, shiftId]);

  const submit = async () => {
    const value = Number(amount);
    if (!value || value <= 0) {
      setErr('Enter how much was taken out.');
      return;
    }
    if (!reason.trim()) {
      setErr('A reason is required — this is what makes the cash-up explainable.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await apiClient.recordPettyCash(branchId, value, reason.trim(), shiftId);
      setAmount('');
      setReason('');
      await load();
      onChange?.();
    } catch (e: any) {
      const d = e?.response?.data?.message;
      setErr(Array.isArray(d) ? d.join(', ') : d || 'Could not record it');
    } finally {
      setBusy(false);
    }
  };

  const total = entries.reduce((s, e) => s + Number(e.amount), 0);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b flex justify-between items-baseline">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Petty cash</h3>
          <p className="text-sm text-gray-500">
            {shiftId ? 'Taken out during this shift' : 'Recent — open a shift to tie entries to it'}
          </p>
        </div>
        <span className="tabular-nums font-bold text-lg text-red-600">− R {total.toFixed(2)}</span>
      </div>

      <div className="p-6 grid grid-cols-1 sm:grid-cols-[140px_1fr_auto] gap-3 items-end border-b">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
          <div className="flex items-center gap-1.5">
            <span className="text-gray-500 font-medium">R</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg tabular-nums font-semibold focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">What for</label>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. milk from the shop, taxi fare"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={submit}
          disabled={busy}
          className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold px-5 py-2 rounded-lg whitespace-nowrap"
        >
          {busy ? 'Saving…' : 'Record'}
        </button>
      </div>

      {err && <p className="px-6 pt-3 text-sm text-red-600">{err}</p>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <tbody>
            {loading ? (
              <tr><td className="py-8 text-center text-gray-500">Loading…</td></tr>
            ) : entries.length === 0 ? (
              <tr><td className="py-8 text-center text-gray-500">Nothing taken out yet.</td></tr>
            ) : (
              entries.map((e) => (
                <tr key={e.id} className="border-b last:border-0">
                  <td className="py-2.5 px-6">
                    <p className="font-medium text-gray-900">{e.reason}</p>
                    <p className="text-xs text-gray-500">
                      {e.staffName ? `${e.staffName} · ` : ''}
                      {new Date(e.createdAt).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}
                    </p>
                  </td>
                  <td className="py-2.5 px-6 text-right tabular-nums font-semibold text-red-600 w-28">
                    − R {Number(e.amount).toFixed(2)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
